import { X, Minus, Plus, Trash2 } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { getFirstImage, getEffectivePrice } from "@/hooks/useProducts";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import GlossyIcon from "@/components/GlossyIcon";
import FreeShippingBar from "@/components/FreeShippingBar";
import CartUpsell from "@/components/CartUpsell";

const CartDrawer = () => {
  const { items, isOpen, setIsOpen, removeItem, updateQuantity, totalItems, subtotal, clearCart } = useCart();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60]">
      <div className="absolute inset-0 bg-foreground/40" onClick={() => setIsOpen(false)} />
      <div className="absolute top-0 right-0 bottom-0 w-full max-w-md bg-background shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h2 className="font-display text-lg font-bold flex items-center gap-2">
            <GlossyIcon name="cart" size={22} />
            Cart ({totalItems})
          </h2>
          <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-secondary rounded transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto p-5">
          {items.length === 0 ? (
            <div className="text-center py-16">
              <GlossyIcon name="cart" size={48} className="mx-auto opacity-40 mb-4" />
              <p className="text-muted-foreground font-body">Your cart is empty</p>
              <Button variant="outline" className="mt-4" onClick={() => setIsOpen(false)}>
                Continue Shopping
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((item) => {
                const price = getEffectivePrice(item.product);
                return (
                  <div key={`${item.product.id}-${item.variantLabel}`} className="flex gap-4 bg-card rounded-lg p-3 border border-border">
                    <img
                      src={getFirstImage(item.product.images)}
                      alt={item.product.name}
                      className="w-20 h-20 rounded object-cover bg-muted shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-body text-sm font-medium truncate">{item.product.name}</h3>
                      {item.variantLabel && (
                        <p className="text-xs text-muted-foreground font-body mt-0.5">{item.variantLabel}</p>
                      )}
                      <p className="text-sm font-bold font-body text-primary mt-1">£{price.toFixed(2)}</p>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center border border-input rounded">
                          <button onClick={() => updateQuantity(item.product.id, item.quantity - 1, item.variantLabel)} className="p-1.5 hover:bg-secondary transition-colors">
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="px-3 text-xs font-body font-medium">{item.quantity}</span>
                          <button onClick={() => updateQuantity(item.product.id, item.quantity + 1, item.variantLabel)} className="p-1.5 hover:bg-secondary transition-colors">
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        <button onClick={() => removeItem(item.product.id, item.variantLabel)} className="p-1.5 text-destructive hover:bg-destructive/10 rounded transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Upsell suggestions */}
              <CartUpsell onClose={() => setIsOpen(false)} compact />
            </div>
          )}
        </div>

        {/* Footer / Summary */}
        {items.length > 0 && (
          <div className="border-t border-border p-5 space-y-4">
            <FreeShippingBar subtotal={subtotal} />
            <div className="flex justify-between font-body">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="font-bold">£{subtotal.toFixed(2)}</span>
            </div>
            <p className="text-xs text-muted-foreground font-body">Shipping calculated at checkout</p>
            <Link to="/checkout" onClick={() => setIsOpen(false)}>
              <Button className="w-full" size="lg">
                Checkout · £{subtotal.toFixed(2)}
              </Button>
            </Link>
            <div className="flex items-center justify-between">
              <button onClick={() => setIsOpen(false)} className="text-sm text-primary hover:underline font-body transition-colors">
                Continue Shopping
              </button>
              <button onClick={clearCart} className="text-sm text-muted-foreground hover:text-destructive font-body transition-colors">
                Clear cart
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartDrawer;
